import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import { usePlatformPermissions } from '../../../hooks/usePlatformPermissions';
import { AiSalesSubnav, EmptyState, ErrorState, KpiGrid, SalesFunnel, SkeletonBlock, SourceBanner } from '../components/ui';
import { fetchOverview } from '../lib/repository';
import { setAiSalesPreview } from '../lib/source';
import type { SalesOverview } from '../types/contracts';

export default function AiSalesOverviewPage() {
  const navigate = useNavigate();
  const { can } = usePlatformPermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<SalesOverview | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    const r = await fetchOverview(from || undefined, to || undefined);
    setLoading(false);
    if (!r.ok) { setError(r.message); return; }
    setData(r.data);
  };

  useEffect(() => { void load(); }, [from, to]);

  const empty = !loading && data && data.kpis.every((k) => !k.value) && data.recent_conversations.length === 0;

  return (
    <AdminLayout>
      <AiSalesSubnav />
      <SourceBanner />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-black text-gray-900">AI Sales Agent</h1>
          <p className="text-sm text-gray-500">How well is our AI salesperson performing?</p>
        </div>
        <div className="flex gap-2">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
        </div>
      </div>
      {loading ? <SkeletonBlock rows={6} /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
      {!loading && !error && data ? (
        <div className="space-y-5">
          <KpiGrid items={data.kpis} />
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2"><SalesFunnel stages={data.funnel} /></div>
            <div className="lg:col-span-3 rounded-2xl bg-white border border-gray-100 p-5">
              <h3 className="text-sm font-bold mb-3">Recent conversations</h3>
              {data.recent_conversations.length === 0 ? <p className="text-sm text-gray-400">No conversations yet.</p> : (
                <ul className="divide-y divide-gray-50">
                  {data.recent_conversations.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => navigate(`/ai-sales/inbox/${c.id}`)} className="w-full text-start py-3">
                        <div className="flex justify-between gap-2">
                          <span className="font-semibold text-sm">{c.lead_name}</span>
                          <span className="text-[11px] text-gray-400">{c.channel} · {c.status}</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{c.last_message}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-gray-100 p-5">
            <h3 className="text-sm font-bold mb-3">Hot leads</h3>
            {data.hot_leads.length === 0 ? <p className="text-sm text-gray-400">No hot leads right now.</p> : (
              <div className="grid md:grid-cols-2 gap-3">
                {data.hot_leads.map((l) => (
                  <div key={l.id} className="border border-gray-100 rounded-xl p-4">
                    <div className="flex justify-between">
                      <div>
                        <p className="font-bold text-sm">{l.name}</p>
                        <p className="text-xs text-gray-500">{l.business}</p>
                      </div>
                      <span className="text-sm font-black text-teal-700">{l.score}</span>
                    </div>
                    <p className="text-xs mt-2 text-gray-600">Interested: {l.interested_plan || '—'}</p>
                    <p className="text-xs text-gray-600">Objection: {l.objection || '—'}</p>
                    <p className="text-xs text-gray-600">Next: {l.next_action || '—'}</p>
                    <button type="button" onClick={() => navigate(`/ai-sales/leads/${l.id}`)} className="mt-3 text-xs font-bold text-teal-700">View Lead</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {empty ? (
            <EmptyState
              title="No conversations yet."
              body="Connect a sales channel to start receiving DressnMore leads."
              action={can('ai_sales.manage') ? (
                <button type="button" className="px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-bold" onClick={() => { setAiSalesPreview(true); window.location.reload(); }}>
                  Preview sample data
                </button>
              ) : null}
            />
          ) : null}
        </div>
      ) : null}
    </AdminLayout>
  );
}
